// Shared configuration for Effortless Gym's two physical sites.
// Keep this as the single source of truth for credit pricing and site
// facts so the membership calculator, How It Works tables, and the two
// site pages never drift out of sync with each other.

export type SiteId = "bournemouthAirport" | "poole";

export interface CreditCosts {
  shared: { 30: number; 60: number; 90: number };
  exclusive: { 30: number; 60: number; 90: number };
}

export interface SiteInfo {
  id: SiteId;
  name: string;
  shortName: string;
  path: string;
  maxCapacity: number;
  address: string[];
  /** Full address as a single line, for calendar invites, emails and directions links. */
  addressLine: string;
  postcode: string;
  creditCosts: CreditCosts;
  status: "open" | "coming-soon";
}

// Shared-session rates are identical at both sites. Only Exclusive Use
// rates differ by site - Poole's exclusive credit costs are higher to
// reflect its larger 6-person capacity and equipment set.
function durationCosts(rate: number): CreditCosts["shared"] {
  return { 30: rate, 60: rate * 2, 90: rate * 3 };
}

const SHARED_CREDIT_COSTS = durationCosts(1);

function creditCosts(exclusiveRate: number): CreditCosts {
  return {
    shared: SHARED_CREDIT_COSTS,
    exclusive: durationCosts(exclusiveRate),
  };
}

export const SITES: Record<SiteId, SiteInfo> = {
  bournemouthAirport: {
    id: "bournemouthAirport",
    name: "Effortless Gym - Bournemouth Airport",
    shortName: "Bournemouth Airport",
    path: "/gym-bournemouth-airport",
    maxCapacity: 4,
    address: [
      "76 Basepoint Business Centre",
      "Aviation Park West, Hurn",
      "Christchurch, BH23 6NX",
    ],
    addressLine: "76 Basepoint Business Centre, Aviation Park West, Hurn, Christchurch BH23 6NX",
    postcode: "BH23 6NX",
    creditCosts: creditCosts(4),
    status: "open",
  },
  poole: {
    id: "poole",
    name: "Effortless Gym - Poole",
    shortName: "Poole",
    path: "/poole",
    maxCapacity: 6,
    address: ["Unit D2, Arena Business Centre", "Holyrood Cl, Poole, BH17 7FJ"],
    addressLine: "Unit D2, Arena Business Centre, Holyrood Cl, Poole BH17 7FJ",
    postcode: "BH17 7FJ",
    creditCosts: creditCosts(6),
    status: "open",
  },
};

export const SITE_LIST = Object.values(SITES);

export interface CreditSession {
  type: keyof CreditCosts;
  duration: 30 | 60 | 90;
  frequency: number;
}

export function calculateSessionCredits(siteId: SiteId, type: CreditSession["type"], duration: CreditSession["duration"]): number {
  return (duration / 30) * SITES[siteId].creditCosts[type][30];
}

export function calculateCreditUsage(siteId: SiteId, sessions: readonly CreditSession[]) {
  const weeklyCredits = sessions.reduce(
    (sum, session) => sum + calculateSessionCredits(siteId, session.type, session.duration) * session.frequency,
    0,
  );
  return { weeklyCredits, monthlyCredits: weeklyCredits * 4 };
}
